/**
 * PageActions Component - Standardized action button container
 * Provides consistent placement and styling for page-level actions
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

// Action alignment options
export const PageActionsAlign = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
  SPACE_BETWEEN: 'space-between'
};

const PageActions = ({
  children,
  align = PageActionsAlign.RIGHT,
  className = '',
  style = {},
  gap = 'md', // 'sm', 'md', 'lg'
  fullWidth = false,
  sticky = false, // Stick to bottom of viewport
  ...props
}) => {
  const { theme } = useTheme();

  const getAlignment = () => {
    switch (align) {
      case PageActionsAlign.LEFT:
        return 'flex-start';
      case PageActionsAlign.CENTER:
        return 'center';
      case PageActionsAlign.RIGHT:
        return 'flex-end';
      case PageActionsAlign.SPACE_BETWEEN:
        return 'space-between';
      default:
        return 'flex-end';
    }
  };

  const getGap = () => {
    switch (gap) {
      case 'sm':
        return theme.spacing.sm;
      case 'lg':
        return theme.spacing.lg;
      default:
        return theme.spacing.md;
    }
  };

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: getAlignment(),
    gap: getGap(),
    flexWrap: 'wrap',
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.xl, // Add spacing below buttons before next content
    paddingTop: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.border}`,
    width: fullWidth ? '100%' : 'auto',
    ...(sticky ? {
      position: 'sticky',
      bottom: 0,
      background: theme.colors.background,
      zIndex: 10,
      boxShadow: `0 -2px 8px ${theme.colors.shadow}`,
      padding: theme.spacing.lg,
      marginTop: 0,
      marginLeft: `-${theme.spacing.lg}`,
      marginRight: `-${theme.spacing.lg}`,
      width: `calc(100% + ${theme.spacing.xl})`
    } : {}),
    ...style
  };

  return (
    <div
      className={`page-actions ${className}`}
      style={baseStyles}
      {...props}
    >
      {children}
    </div>
  );
};

// Sub-component for grouped actions (e.g., primary actions on right, secondary on left)
export const PageActionsGroup = ({
  children,
  align = PageActionsAlign.LEFT,
  className = '',
  style = {},
  gap = 'sm'
}) => {
  const { theme } = useTheme();

  const getGap = () => {
    switch (gap) {
      case 'sm':
        return theme.spacing.sm;
      case 'lg':
        return theme.spacing.lg;
      default:
        return theme.spacing.md;
    }
  };

  const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: getGap(),
    flexWrap: 'wrap',
    ...style
  };

  return (
    <div
      className={`page-actions-group ${className}`}
      style={groupStyles}
    >
      {children}
    </div>
  );
};

export default PageActions;
